import { useRef } from 'react';

import { Box, useColorModeValue } from '@ecoinvent/ui';
import { ReactFlow } from '@xyflow/react';

import { useGraphDownload } from 'hooks/useGraphDownload';

import { GRAPH_HEIGHT } from './constants';
import DatasetNode from './DatasetNode';
import FlowEdge from './FlowEdge';
import FlowNode from './FlowNode';
import GraphControls from './GraphControls';
import GraphLegend from './GraphLegend';
import { FlowGraphCanvasProps } from './types';
import { useAutoLayout } from './useAutoLayout';
import { useGraphLayout } from './useGraphLayout';

const nodeTypes = { flowNode: FlowNode, datasetNode: DatasetNode };
const edgeTypes = { flowEdge: FlowEdge };

const FlowGraphCanvas = ({
  datasetId,
  inputs,
  outputs,
  flowTabType,
  isFullScreen,
  setIsFullScreen,
  onFlowClick,
}: FlowGraphCanvasProps) => {
  const bgColor = useColorModeValue('blue.50', 'gray.800');

  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);

  const { nodes, edges } = useGraphLayout({ datasetId, inputs, outputs, flowTabType, onFlowClick });

  useAutoLayout({ inputs, outputs });

  const handleDownload = useGraphDownload(containerRef, controlsRef, 'flow-graph.png');

  return (
    <Box
      ref={containerRef}
      w="full"
      h={isFullScreen ? '100%' : `${GRAPH_HEIGHT}px`}
      bg={bgColor}
      borderRadius={isFullScreen ? 'none' : 'lg'}
      position="relative"
      overflow="hidden"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        zoomOnScroll
        zoomOnPinch
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        onNodeClick={() => {}}
        proOptions={{ hideAttribution: true }}
      />

      <GraphLegend outputs={outputs} />

      <Box ref={controlsRef} position="absolute" bottom={4} right={4} zIndex={10}>
        <GraphControls
          isFullScreen={isFullScreen}
          onToggle={() => setIsFullScreen(!isFullScreen)}
          onDownload={handleDownload}
        />
      </Box>
    </Box>
  );
};

export default FlowGraphCanvas;
